/**
 * The purity stamp: a bar of metal carrying the chart's measured error.
 *
 * ============================ WHAT THIS IS FOR =================================
 * Every chart in the library is measured — `verify/abstraction-error.ts` deals two
 * hundred thousand hands per node and reports how far the solver's model sits from the
 * real game, as a fraction of the pot. That number reaches this component; this
 * component is how a person sees it without reading a table.
 *
 * The form is an assay stamp, and that is not decoration. A hallmark on metal is
 * structurally a maker's mark plus a purity figure — the number is PART of the stamp.
 * That is the property worth having, because the failure mode this product exists to
 * avoid is a grade that floats free of a measurement. "GTO" is the industry's example;
 * a tier name with no number behind it would be ours.
 *
 * So: the metal carries the tier, the figure carries the truth, and `Purity` will not
 * render one without the other.
 *
 * ## Why a bar and not the mark
 *
 * The first draft used the BluffBusters B, filled for the tight tier and outlined for
 * the loose one. Mocked at size, the outline failed: the Rye B has split serifs and
 * tight counters, and a stroke heavy enough to see at 16px closes them into a lozenge.
 * `mark.tsx` already documents that size floor for card backs, where it drops the mark
 * rather than draw it badly. A bar is a silhouette and survives where a letterform does
 * not — so the mark keeps its own job and the bar takes this one.
 *
 * ## Why these two colours specifically
 *
 * Gold and silver have to be told apart WITHOUT colour, or the badge fails for a
 * colour-blind reader and on any greyscale display. The first pair — #B28C4A against
 * #9AA3AE — had a relative-luminance gap of 0.074 and a metal-to-metal contrast ratio
 * of 1.22:1, which is to say they were the same object in greyscale. The silver here is
 * lightened to #B7BEC7: gap 0.223, ratio 1.66:1, and both still clear 4.5:1 against the
 * page. `tests/purity.test.ts` recomputes all of that and fails if either drifts.
 * ================================================================================
 */

/** Face, top and shadow for each metal. Kept here so the test can assert on them. */
export const METALS = {
  gold:   { top: "#D8BC7A", face: "#B28C4A", edge: "#8A6B35" },
  silver: { top: "#E2E7EC", face: "#B7BEC7", edge: "#8D95A0" },
} as const;

/** Struck lettering. 5.0:1 on gold, 8.4:1 on silver — AA at normal text size. */
export const STAMP_INK = "#2E210B";

export type PurityTier = keyof typeof METALS;

/** The label for a tier. The ceiling, not the chart's own figure. */
export const TIER_LABEL: Record<PurityTier, string> = {
  gold: "≤0.24%",
  silver: "≤0.50%",
};

function Bar({ tier }: { tier: PurityTier }) {
  const m = METALS[tier];
  return (
    <svg className="bar" viewBox="0 0 120 74" aria-hidden="true" focusable="false">
      <polygon points="18,20 102,20 120,52 0,52" fill={m.face} />
      <polygon points="18,20 102,20 96,10 24,10" fill={m.top} />
      <polygon points="102,20 120,52 114,44 96,10" fill={m.edge} />
      <polygon points="18,20 0,52 6,44 24,10" fill={m.edge} />
      <rect x="26" y="30" width="68" height="3.2" rx="1.6" fill={m.edge} opacity=".5" />
      <rect x="26" y="38" width="46" height="3.2" rx="1.6" fill={m.edge} opacity=".36" />
    </svg>
  );
}

/**
 * The inline stamp.
 *
 * `measured` is the chart's own figure and is what the title carries, so hovering gives
 * the exact number rather than the tier's ceiling. A tier with no measurement renders
 * NOTHING — see the guard below.
 */
export function Purity({ tier, measured }: {
  tier: PurityTier | null; measured: number | null | undefined;
}) {
  /**
   * THE GUARD. A stamp without a figure is the exact thing this component exists to
   * prevent, so an unmeasured chart gets no bar — not a bar with a blank where the
   * number goes. `tests/purity.test.ts` pins it.
   */
  if (!tier || typeof measured !== "number" || !Number.isFinite(measured)) return null;
  return (
    <span className={`purity purity-${tier}`}
          title={`Measured at ${(measured * 100).toFixed(2)}% of pot`}>
      <Bar tier={tier} />
      <b>{TIER_LABEL[tier]}</b>
      <span>of pot</span>
    </span>
  );
}

/**
 * The large struck bar, with the figure debossed into the metal.
 *
 * For the About page and anywhere else with room. Not for a spot header: at badge size
 * the stamp is illegible, which is why the inline form puts the number beside the bar
 * instead of on it.
 */
export function PurityIngot({ tier, label }: { tier: PurityTier; label?: string }) {
  const m = METALS[tier];
  const text = label ?? TIER_LABEL[tier];
  return (
    <svg className="ingot" viewBox="0 0 260 150" role="img"
         aria-label={`${text} of pot`}>
      <polygon points="40,44 220,44 260,116 0,116" fill={m.face} />
      <polygon points="40,44 220,44 208,24 52,24" fill={m.top} />
      <polygon points="220,44 260,116 248,100 208,24" fill={m.edge} />
      <polygon points="40,44 0,116 12,100 52,24" fill={m.edge} />
      <g fill={STAMP_INK}>
        <text x="130" y="78" textAnchor="middle" className="ingot-num">{text}</text>
        <text x="130" y="99" textAnchor="middle" className="ingot-sub">OF POT</text>
      </g>
    </svg>
  );
}
